import React, { useCallback, useMemo, useState } from 'react';

import { theme } from '~/theme';

import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import { green } from '@mui/material/colors';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import useToggleState from '~/hooks/useToggleState';

import { MergedRate, Rate } from '~/services/AccommodationService';

import { pluralizeToString } from '~/utils/stringUtils';

import RateDetailsModal from '../RateDetailsModal';
import RateTypeBadge from '../RateTypeBadge';
import { formatMargin, formatPrice } from '../utils/formatPrice';

interface GroupedRatesProps {
  winningRate: Rate;
  mergedRates: Array<MergedRate>;
  currency: string;
}

export default function GroupedRates({ winningRate, mergedRates, currency }: GroupedRatesProps) {
  const { isToggled: isOpen, toggleOn: openModal, toggleOff: closeModal } = useToggleState(false);
  const {
    isToggled: isRateDetailsModalOpen,
    toggleOn: openRateDetailsModal,
    toggleOff: closeRateDetailsModal,
  } = useToggleState(false);
  const [selectedRate, setSelectedRate] = useState<Rate | null>(null);

  const rows = useMemo(() => {
    return [winningRate].concat(
      mergedRates.map((mr) => ({
        ...(mr.rate as Rate),
        description: mr.description,
        mergeReason: mr.mergeReason,
      })),
    );
  }, [winningRate, mergedRates]);

  const handleDetailsClick = useCallback(
    (rate: Rate) => {
      setSelectedRate(rate);
      openRateDetailsModal();
    },
    [openRateDetailsModal],
  );

  if (mergedRates.length === 0) {
    return null;
  }

  const columns: Array<GridColDef> = [
    {
      field: 'rateType',
      headerName: 'Rate Type',
      flex: 1,
      renderCell: (params) => (
        <Box>
          <RateTypeBadge rate={params.row} />
        </Box>
      ),
    },
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'basePrice',
      headerName: 'Base Price',
      flex: 1,
      renderCell: (params) => formatPrice(params.row.total?.beforeTaxesFeesPrice as number, currency),
    },
    {
      field: 'taxesAndFees',
      headerName: 'Taxes & Fees',
      flex: 1,
      renderCell: (params) => formatPrice(params.row.total?.taxesAndFees as number, currency),
    },
    {
      field: 'propertyFees',
      headerName: 'Payable at Property',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => {
        const hasPropertyFees = params.row.total?.propertyFees > 0;
        return hasPropertyFees ? formatPrice(params.row.total?.propertyFees as number, currency) : 'None';
      },
    },
    {
      field: 'totalPrice',
      headerName: 'Total Price',
      // flex: 1,
      renderCell: (params) => {
        return formatPrice((params.row.total?.sellingPrice || 0) + (params.row.total?.propertyFees || 0), currency);
      },
    },
    {
      field: 'luxplusPrice',
      headerName: 'Luxplus Price',
      flex: 1,
      renderCell: (params) => {
        return formatPrice(params.row.luxPlus?.displayPrice || 0, currency);
      },
    },
    {
      field: 'savings',
      headerName: 'Savings',
      // flex: 1,
      renderCell: (params) => {
        return params.row.total?.discount > 0 ? (
          <Chip label={`${(params.row.total.discount * 100).toFixed(0)}% off`} color="secondary" size="small" />
        ) : (
          'No discount'
        );
      },
    },
    {
      field: 'margin',
      headerName: 'Margin',
      // flex: 1,
      renderCell: (params) => {
        return formatMargin(params.row.total.margin);
      },
    },
    {
      field: 'mergeReason',
      headerName: 'Reason',
      flex: 3,
      renderCell: (params) => {
        if (params.row.mergeReason) {
          return (
            <Tooltip title={params.row.description}>
              <Box>{params.row.mergeReason}</Box>
            </Tooltip>
          );
        }
        return <Box>Winning Rate</Box>;
      },
    },
    {
      field: 'details',
      headerName: 'Details',
      flex: 1,
      renderCell: (params) => (
        <IconButton size="small" color="primary" onClick={() => handleDetailsClick(params.row)}>
          <InfoIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Chip label={pluralizeToString('rate', mergedRates.length)} size="small" color="secondary" onClick={openModal} />
      <Dialog
        open={isOpen}
        onClose={closeModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            position: 'relative',
            overflow: 'visible',
          },
        }}
      >
        <DialogTitle>
          <Stack>{winningRate.groupKey}</Stack>
          <IconButton
            aria-label="close"
            onClick={closeModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 5 },
              },
            }}
            pageSizeOptions={[5, 10, 20]}
            disableColumnFilter
            disableColumnMenu
            disableDensitySelector
            disableColumnSelector
            hideFooterSelectedRowCount
            getRowClassName={(params) => (params.row.id === rows[0].id ? 'Mui-br' : 'Mui-nonbr')}
            sx={{
              '.Mui-br': {
                bgcolor: green[100],
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Close</Button>
        </DialogActions>
      </Dialog>
      {isRateDetailsModalOpen && (
        <Dialog open={isRateDetailsModalOpen} onClose={closeRateDetailsModal} maxWidth="md" fullWidth>
          <DialogTitle>
            Rate Details
            <IconButton
              aria-label="close"
              onClick={closeRateDetailsModal}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <RateDetailsModal rate={selectedRate} currency={currency} />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeRateDetailsModal}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
